import React, { useState } from 'react';
import { startOfDay, endOfDay, isValid as isValidDate } from 'date-fns';
import { DatePicker } from '../DatePicker';
import { displayDate } from '../../helpers/common';

export const DateField = ({
  dateFormat,
  onChange,
  onBlur,
  onClickOutside,
  onCalendarClose,
  onSelect,
  placeholder = '',
  name = '',
  timestampValue = null,
  label = '',
  inputClassName,
  containerClassName,
  labelClassName = '',
  errorMessage = '',
  errorMessageClassName = '',
  isRequired = false,
  isVisible = true,
  setFormErrorMessages,
  id,
  minDate = startOfDay(new Date(1900, 0, 1)),
  maxDate,
}) => {
  const [error, setError] = useState('');

  const displayError = (message) => {
    setError(message);

    if (name) {
      setFormErrorMessages?.(name, message);
    }
  };

  const validateDate = (dateTimestamp) => {
    const fieldName = label || 'This field';

    if (!dateTimestamp) {
      if (isRequired) {
        displayError(`${fieldName} is required.`);
        return;
      }

      displayError('');
      return;
    }

    if (!isValidDate(new Date(dateTimestamp))) {
      displayError(`${fieldName} is not a valid date.`);
      return;
    }

    if (
      isValidDate(minDate) &&
      startOfDay(minDate).getTime?.() > dateTimestamp
    ) {
      displayError(`Minimum date is ${displayDate(minDate.getTime())}.`);
      return;
    }

    if (isValidDate(maxDate) && endOfDay(maxDate).getTime?.() < dateTimestamp) {
      displayError(`Maximum date is ${displayDate(maxDate.getTime())}.`);

      return;
    }

    displayError('');
  };

  const onChangeHandler = (selectedDate) => {
    const dateTimestamp = selectedDate?.getTime?.() || null;
    const dateValue =
      dateTimestamp && isValidDate(new Date(dateTimestamp))
        ? new Date(dateTimestamp)
        : null;

    onChange?.({
      target: {
        type: 'date',
        name,
        value: dateValue,
        valueAsNumber: dateValue?.getTime?.() || null,
      },
    });
  };

  const onBlurHandler = () => {
    validateDate(timestampValue);
    onBlur?.();
  };

  const onClickOutsideHandler = () => {
    validateDate(timestampValue);
    onClickOutside?.();
  };

  const onSelectHandler = () => {
    validateDate(timestampValue);
    onSelect?.();
  };

  const onCalendarCloseHandler = () => {
    validateDate(timestampValue);
    onCalendarClose?.();
  };

  const labelId = id ? `${id}-label` : '';
  const displayErrorMessage = errorMessage || error;

  if (!isVisible) {
    return null;
  }

  return (
    <div className={containerClassName}>
      {label && (
        <div id={labelId} className={labelClassName}>
          {label}
        </div>
      )}
      <DatePicker
        id={id}
        timestampValue={timestampValue}
        placeholder={placeholder}
        onChange={onChangeHandler}
        onBlur={onBlurHandler}
        onClickOutside={onClickOutsideHandler}
        onCalendarClose={onCalendarCloseHandler}
        onSelect={onSelectHandler}
        dateFormat={dateFormat}
        inputClassName={inputClassName}
        containerClassName={containerClassName}
      />
      {displayErrorMessage && (
        <div className={errorMessageClassName}>{displayErrorMessage}</div>
      )}
    </div>
  );
};
